import { formatExecutionTime } from '@fruitful/fruitils';
import { RECURRING_TRANSFER_FREQUENCY_DESCRIPTION_MAP } from '@fruitful/types';
import * as React from 'react';
import {
    ArrowRightIcon,
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    CalendarClockIcon,
    ChevronRightIcon,
    Icon,
    isNonMobileWeb,
    Separator,
    SplitIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../../../common/misc';
import { centsToFormattedDollars } from '../../../../../common/utils/bankingUtils';
import {
    formatWithoutTimezone,
    SHORT_READABLE_DATE_FORMAT,
} from '../../../../../common/utils/dateUtils';
import type { PaymentScheduleRenderDetails } from '../../../../hooks/useGetPaymentSchedules';
import { LEGACY_RECURRING_EXECUTION_TIME_24H } from '../../../Disclosures';
import { TransferAccountIcon } from '../../TransferIcons';

interface RecurringTransferLineItemProps {
    transfer: PaymentScheduleRenderDetails;
    onPress: () => void;
    isLast: boolean;
}

const RecurringTransferLineItem = ({
    transfer,
    onPress,
    isLast,
}: RecurringTransferLineItemProps) => {
    const {
        fromAccount,
        toAccount,
        amount,
        nextPaymentDate,
        recurringFrequency,
        recurringDescription,
        executionTime,
        metadata,
    } = transfer;

    return (
        <YStack alignItems="center">
            <YStack
                width="100%"
                paddingVertical={24}
                onPress={onPress}
                cursor="pointer"
                hoverStyle={{ backgroundColor: '$gray100' }}
                $sm={{ paddingHorizontal: 16, borderRadius: 12 }}
            >
                <XStack>
                    <XStack alignItems="center" marginRight={20}>
                        <TransferAccountIcon size="md" account={fromAccount} />
                        <View
                            marginTop={10}
                            marginLeft={-8}
                            marginRight={-8}
                            alignSelf="flex-start"
                            padding={6}
                            borderRadius={100}
                            borderWidth={1}
                            borderColor="$gray100"
                            backgroundColor="$white"
                            zIndex={1}
                        >
                            <Icon
                                icon={ChevronRightIcon}
                                size={12}
                                color="$gray1200"
                            />
                        </View>
                        <TransferAccountIcon size="md" account={toAccount} />
                    </XStack>
                    <YStack>
                        <BodyLargeEmphasized>
                            {centsToFormattedDollars(amount)}
                        </BodyLargeEmphasized>
                        <BodyMediumRegular color="$gray1000">
                            Recurring{' '}
                            {RECURRING_TRANSFER_FREQUENCY_DESCRIPTION_MAP[
                                recurringFrequency
                            ].toLowerCase()}
                        </BodyMediumRegular>
                    </YStack>
                    <View marginLeft="auto">
                        <Icon
                            icon={ArrowRightIcon}
                            size={16}
                            color="$gray1200"
                        />
                    </View>
                </XStack>
                {!isEmpty(recurringDescription) && (
                    <BodyLargeRegular marginVertical={18} marginHorizontal={4}>
                        {recurringDescription}
                    </BodyLargeRegular>
                )}
                {nextPaymentDate && (
                    <XStack
                        alignItems="flex-start"
                        justifyContent="space-between"
                        marginTop={isEmpty(recurringDescription) ? 18 : 0}
                    >
                        <XStack alignItems="center" gap={12}>
                            <View
                                backgroundColor="$gray100"
                                padding={8}
                                borderRadius={8}
                            >
                                <Icon
                                    icon={CalendarClockIcon}
                                    size={16}
                                    color="$gray1100"
                                />
                            </View>
                            <BodyMediumRegular>
                                Next transfer on
                            </BodyMediumRegular>
                        </XStack>
                        <YStack alignItems="flex-end">
                            <BodyMediumEmphasized>
                                {formatWithoutTimezone(
                                    nextPaymentDate.execution_date,
                                    SHORT_READABLE_DATE_FORMAT,
                                )}
                            </BodyMediumEmphasized>
                            <BodySmallRegular color="$gray1000">
                                {formatExecutionTime(
                                    executionTime ??
                                        LEGACY_RECURRING_EXECUTION_TIME_24H,
                                )}
                            </BodySmallRegular>
                        </YStack>
                    </XStack>
                )}
                {!toAccount.isExternal && (
                    <XStack
                        alignItems="center"
                        justifyContent="space-between"
                        marginTop={isEmpty(recurringDescription) ? 18 : 4}
                    >
                        <XStack alignItems="center" gap={12}>
                            <View
                                backgroundColor="$gray100"
                                padding={8}
                                borderRadius={8}
                                rotate="180deg"
                            >
                                <Icon
                                    icon={SplitIcon}
                                    size={16}
                                    color="$gray1100"
                                />
                            </View>
                            <BodyMediumRegular>
                                Apply Income Split rules
                            </BodyMediumRegular>
                        </XStack>
                        <BodyMediumEmphasized>
                            {metadata?.applyIncomeSplit ? 'Yes' : 'No'}
                        </BodyMediumEmphasized>
                    </XStack>
                )}
            </YStack>
            {!isLast && <Separator width={isNonMobileWeb ? '95%' : '100%'} />}
        </YStack>
    );
};

export default RecurringTransferLineItem;
